import { LegalPageChrome } from '@/components/LegalPageChrome';
import { FileText, Mail, Building, AlertTriangle, Mic } from 'lucide-react';
import { Card } from '@/components/ui/card';

const Terms = () => {

  return (
    <LegalPageChrome>
      <main className="container mx-auto px-4 pt-24 pb-16 max-w-5xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <FileText className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary">Legal Terms</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground">Terms of Use</h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            CAIRE B.V. — Effective Date: September 8, 2026
          </p>
        </div>

        {/* Company Info Card */}
        <Card className="p-8 mb-16 bg-card/30">
          <h2 className="text-2xl font-bold mb-6 text-foreground flex items-center gap-3">
            <Building className="w-6 h-6 text-primary" />
            Company Information
          </h2>
          <div className="grid md:grid-cols-2 gap-6 text-muted-foreground">
            <div>
              <p className="font-semibold text-foreground mb-2">Company</p>
              <p>CAIRE B.V.</p>
            </div>
            <div>
              <p className="font-semibold text-foreground mb-2">Address</p>
              <p>Van der Takstraat 8<br />3071LL Rotterdam<br />The Netherlands</p>
            </div>
            <div>
              <p className="font-semibold text-foreground mb-2">Chamber of Commerce (KvK)</p>
              <p>97051438</p>
            </div>
            <div>
              <p className="font-semibold text-foreground mb-2">VAT</p>
              <p>NL867891348B01</p>
            </div>
            <div className="md:col-span-2">
              <p className="font-semibold text-foreground mb-2">Contact Email</p>
              <div className="flex flex-col gap-1">
                <a href="mailto:legal@meetcaire.com" className="text-primary hover:underline flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  legal@meetcaire.com
                </a>
                <a href="mailto:privacy@meetcaire.com" className="text-primary hover:underline flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  privacy@meetcaire.com
                </a>
              </div>
            </div>
          </div>
        </Card>

        {/* Scope */}
        <Card className="p-8 mb-16 bg-card/50 border-primary/20">
          <p className="text-muted-foreground leading-relaxed">
            These Terms of Use ("Terms") govern access to and use of:<br />
            (a) the CAIRE website at meetcaire.com,<br />
            (b) the CAIRE Platform, including the dashboard, livestream monitoring and analytics, session scheduling, host management, performance scoring, optional speaker detection, reporting, APIs, and integrations,<br />
            (c) the CAIRE OBS Companion desktop application used to capture host audio during a livestream session,<br />
            (d) the CAIRE Host Portal used by livestream hosts, and<br />
            (e) CAIRE AI Chat and messaging add-ons available through live chat, WhatsApp, and other communication channels (together, the "Services").
          </p>
          <p className="text-foreground font-semibold mt-6">
            By accessing or using the Services, you agree to be bound by these Terms.
          </p>
        </Card>

        {/* Main Content */}
        <div className="space-y-12 text-muted-foreground">

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">1. Scope of Users</h2>
            <p className="mb-4">These Terms apply to:</p>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Visitors to meetcaire.com</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Brands, agencies, and their teams using the CAIRE dashboard or platform ("Customers")</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Livestream hosts using the Host Portal or the OBS Companion ("Hosts")</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>End users who interact with CAIRE AI Chat or messaging channels</span>
              </li>
            </ul>
            <p className="mt-4">If you use CAIRE on behalf of a company, you represent that you are authorized to accept these Terms for that company.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">2. Privacy</h2>
            <p>Your use of CAIRE is also governed by our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>, which explains what we collect during livestream sessions, how long we keep it, and who it is shared with. By using CAIRE, you acknowledge that you have read and understood the Privacy Policy.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground flex items-center gap-3">
              <Mic className="w-6 h-6 text-primary" />
              3. Livestream Monitoring — Conditions of Use (Important)
            </h2>
            <Card className="p-6 bg-card/40 border-primary/20">
              <p className="mb-4 text-foreground font-semibold">CAIRE monitors people at work. Access to these features is conditional on the following.</p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Authorization.</strong> You may only monitor livestream sessions performed by Hosts you employ or engage, or for whom you have written authorization from their employer or agency.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Prior notice to Hosts.</strong> You must inform each Host, before monitoring begins, that sessions are monitored, what is captured, what is measured, how long it is retained, and how the results will be used. Covert monitoring is prohibited.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Legal basis and consultation.</strong> You are responsible for establishing a valid legal basis for monitoring and for completing any assessment or consultation your jurisdiction requires, including a Data Protection Impact Assessment and works council or employee representative consultation where applicable.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Scope limits.</strong> CAIRE captures the Host's microphone audio during an authorized session. It does not capture video and does not produce session recordings. You must not attempt to use the Services for continuous, ambient, or off-session monitoring of any person.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Biometric use is confined to speaker detection.</strong> Apart from the optional speaker detection feature, on the conditions set out below, you must not use the Services or data exported from them to biometrically identify, verify, or track any individual. You must never use the Services for identity verification, access control, attendance monitoring, or biometric tracking of a person outside the sessions they host.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Termination for breach.</strong> Monitoring people who have not been informed is a material breach of these Terms and may result in immediate suspension.</span>
                </li>
              </ul>
            </Card>

            <h3 className="text-xl font-bold mt-8 mb-4 text-foreground">3.1 Speaker Detection — Additional Conditions</h3>
            <Card className="p-6 bg-card/40 border-primary/20">
              <p className="mb-4 text-foreground font-semibold">
                Speaker detection builds a voiceprint for each enrolled Host so transcripts can show who was speaking.
                A voiceprint is biometric data. The feature is off by default, and switching it on is a deliberate act
                that carries the following conditions.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">You warrant a lawful basis.</strong> By enabling speaker detection you represent and warrant that you have identified a valid condition under Article 9(2) GDPR, or the equivalent under the law applying to each affected Host, and that you have completed any Data Protection Impact Assessment and consultation your jurisdiction requires.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Some jurisdictions may not permit it.</strong> You acknowledge that biometric processing for the purpose of uniquely identifying a person is tightly restricted in several jurisdictions, and that in the Netherlands the UAVG permits it only where necessary for authentication or security purposes. You are responsible for determining whether the feature is lawfully available to you, and you must not enable it where it is not. CAIRE gives no assurance that enabling the feature is lawful in your circumstances.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Specific prior notice.</strong> General notice that sessions are monitored is not sufficient. You must tell each affected Host, before enrollment begins, that a voiceprint will be created from their voice, what it is used for, how long it is kept, and how to have it deleted.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Deletion on request.</strong> You must honor a Host's request to delete their voiceprint without penalty. Resetting a voiceprint removes the stored vector, its enrollment samples, and the associated reference clips, and does not prevent the Host from working.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Purpose limitation.</strong> Voiceprints and any vector derived from them may be used only to label speech within sessions on the CAIRE Platform. You must not export them, match them against any other dataset, use them to verify identity or control access, or use them to draw inferences about a Host's emotion, health, mood, or personality.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Labels are not evidence.</strong> Attribution is probabilistic and can be wrong, especially where Hosts share a microphone or talk over each other. You must not treat a speaker label as proof that a particular person said something, and must not rely on one in a disciplinary, contractual, or legal process without independent verification.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">We may withdraw the feature.</strong> CAIRE may disable speaker detection for an account, a region, or entirely, at any time and without liability, including where we consider its use unlawful or unsafe.</span>
                </li>
              </ul>
            </Card>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-primary" />
              4. AI, Scoring & Alerts Disclaimer (Important)
            </h2>
            <Card className="p-6 bg-card/40 border-primary/20">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span>CAIRE uses artificial intelligence for speech recognition, call-to-action detection, session summaries, performance scoring, and report generation.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Transcription is not verbatim.</strong> Speech recognition accuracy varies with audio quality, accent, language, background noise, and overlapping speech. Transcripts may contain errors and must not be treated as an authoritative record of what was said.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Detection is not guaranteed.</strong> Call-to-action detection, silence detection, and inactivity signals are statistical estimates. CAIRE does not guarantee that any given event will be detected, or that a detected event actually occurred.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Alerts may be delayed, missed, or false.</strong> Real-time alerts depend on network conditions and third-party services. CAIRE is not a safety, compliance, or supervisory system, and you must not rely on it as the only means of overseeing a live session.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Speaker labels are probabilistic.</strong> Where speaker detection is enabled, an utterance may be attributed to the wrong Host or left unlabelled, and any per-Host figure derived from attribution inherits that error. Speaker labels are not evidence of who said what.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Scores are indicators, not judgments.</strong> Host scores, ratings, and rankings are decision-support outputs derived from measured signals. They are not an assessment of a person's ability, character, or conduct, and they may be affected by factors outside the Host's control.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Human review is mandatory.</strong> You must not use CAIRE scores or alerts as the sole basis for terminating, disciplining, demoting, deprioritizing, or reducing the pay of any Host. Any such decision requires meaningful human review, as required by Article 22 GDPR and equivalent local law.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span><strong className="text-foreground">Commercial figures are indicative.</strong> GMV, order, and bonus figures are derived from platform exports and APIs and may be incomplete, delayed, or restated by the platform. They must be reconciled against the platform's own records before being used for payment.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span>CAIRE does not provide professional, legal, financial, medical, or employment advice.</span>
                </li>
              </ul>
            </Card>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">5. Eligibility</h2>
            <p className="mb-4">You may only use CAIRE if:</p>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You are at least 18 years old</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You have the legal capacity to form a contract</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You comply with these Terms and applicable laws</span>
              </li>
            </ul>
            <p className="mt-4 font-semibold text-foreground">CAIRE is a business tool and may not be used by minors. Customers must not onboard Hosts under 18.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">6. Accounts & Access</h2>
            <p className="mb-4">To access the CAIRE dashboard, Host Portal, or OBS Companion, you must:</p>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Provide accurate registration information</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Keep login credentials secure and enable multi-factor authentication where offered</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Be responsible for actions taken under your account</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Grant dashboard access only to personnel with a legitimate need to view host, performance, and payout data</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Notify us immediately of unauthorized access</span>
              </li>
            </ul>
            <p className="mt-4">CAIRE may suspend accounts that violate these Terms.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">7. OBS Companion Licence</h2>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>CAIRE grants you a limited, revocable, non-exclusive, non-transferable licence to install and use the OBS Companion solely to capture Host audio for authorized sessions on the CAIRE Platform.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You must not install the OBS Companion on a device without the knowledge of the person using it, or configure it to start capture without that person's awareness.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You must not modify, decompile, reverse engineer, or redistribute the OBS Companion.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You are responsible for ensuring the capture device and its audio configuration comply with local law.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>The licence terminates automatically when your subscription ends or these Terms are terminated.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">8. Acceptable Use Policy</h2>
            <p className="mb-4">You must not use CAIRE to:</p>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Break laws or regulations, including labour, employment, and data protection law</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Monitor any person who has not been informed that monitoring takes place</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Capture audio of individuals who are not participants in an authorized livestream session</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Derive emotional, health, or personality inferences about a Host, or create or use voiceprints other than through the speaker detection feature on the conditions in section 3</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Take adverse action against a Host based solely on an automated score or alert</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Harvest, resell, or repurpose livestream viewer data, or use it to contact viewers outside the platform</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Upload harmful, defamatory, discriminatory, or abusive content</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Harass, threaten, mislead, or impersonate others</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Distribute malware, spam, or harmful scripts</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Interfere with CAIRE's systems or misuse our APIs</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Reverse engineer or copy the platform</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Bypass usage limits or security features</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Train competing AI products using CAIRE data, transcripts, or scoring outputs</span>
              </li>
            </ul>
            <p className="mt-4 font-semibold text-foreground">Violations may result in immediate termination.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">9. Host Portal Terms</h2>
            <p className="mb-4">If you are a Host using the CAIRE Host Portal:</p>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Your account is provided through the brand or agency you work with. That organization decides what data is held about you, who can see it, and how long it is kept.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Information you submit, including availability, contact details, invoices, and performance screenshots, must be accurate and your own.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Your livestream sessions may be monitored and scored, and you may receive operational WhatsApp messages such as schedule notifications and reminders on your employer's or agency's behalf. Your employer or agency is required to inform you of this in advance.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>If your employer or agency has enabled speaker detection, a voiceprint is created from your session audio so transcripts can show who was speaking. They are required to tell you before this starts, and you can ask them or us to delete it at any time without it affecting your ability to host sessions.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Session briefs, product information, and pricing shown in the Portal are confidential to that organization and must not be shared externally.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>CAIRE does not employ you, set your pay, or decide your schedule. Bonus and payout figures shown in the Portal are calculated from rules set by your employer or agency and are subject to their approval. Questions about pay, scheduling, or performance decisions should be raised with them.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You may ask how a score was calculated, contest it, and request human review. See the Privacy Policy for how to exercise your rights.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">10. Customer Responsibilities</h2>
            <p className="mb-4">If you are a brand or agency using CAIRE:</p>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You act as Data Controller for your Hosts, your team, and your livestream audience. CAIRE acts as Processor on your instructions.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You are responsible for the lawfulness of the monitoring you enable and for informing Hosts before it begins.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You must only upload Host identity, contract, and tax documents you are lawfully entitled to hold, and must remove them when no longer needed.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You are responsible for the accuracy of bonus rules, targets, rates, and payout data you configure, and for reconciling and issuing payments. CAIRE prepares figures; it does not pay Hosts.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You must hold valid authorization for every connected commerce platform and comply with that platform's terms, including limits on viewer data.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You must honor data subject requests from your Hosts and viewers, and must inform end users where an AI agent is assisting them.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You must comply with local employment and worker-monitoring law in every jurisdiction where your Hosts operate.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">11. Intellectual Property</h2>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>CAIRE owns all rights to the Services, its software, brand assets, scoring methodology, workflows, prompt logic, the models it develops, its interface, documentation, and all related intellectual property. Third-party models and services used within the Services remain the property of their respective owners.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You may not copy, modify, resell, distribute, or create derivative works from CAIRE's technology.</span>
              </li>
            </ul>
            <p className="mt-4 mb-4">Content and data you upload or generate through the Services — including product data, session data, transcripts, host records, and reports — remains yours. By using the Services, you grant CAIRE a limited licence to process it only for:</p>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Operating and supporting the Services</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Producing the analytics, alerts, scores, and reports you have requested</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Maintaining service security and integrity</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Improving quality using anonymized data only, with no personal data</span>
              </li>
            </ul>
            <p className="mt-4">CAIRE does not use your data, host audio, or transcripts to train publicly available AI models.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">12. Subscription, Billing & Fees</h2>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Some Services require a paid subscription. Fees, billing cycles, and included features are set out in your order, subscription plan, or enterprise agreement.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Subscription payments are processed by our third-party payment provider under its own terms. CAIRE does not store your payment card details.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Add-on products, including AI Chat, are billed separately from the core platform.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Subscriptions renew automatically unless canceled.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Fees are non-refundable except where required by law.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Late payments may lead to account suspension.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Enterprise pricing may include custom agreements that take precedence over these Terms.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">13. Third-Party Platforms & Integrations</h2>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>The Services integrate with third-party platforms and providers, including TikTok Shop, Shopee, Lazada, Meta, LiveKit, Deepgram, OpenAI, Groq, Google, Anthropic, Amazon Web Services, and Render.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>These services are governed by their own terms, and your use of a connected commerce platform remains subject to that platform's rules on data access, retention, and viewer privacy.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Platforms may change, restrict, or withdraw API access at any time. CAIRE is not liable for reduced functionality, data gaps, or loss of historical data resulting from a platform change.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>CAIRE is not responsible for failures or data loss caused by third-party integrations.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You authorize CAIRE to connect to these services as part of your use of the Services.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">14. Service Availability & Changes</h2>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>CAIRE does not guarantee uninterrupted or error-free service, and does not guarantee that every live session will be captured, transcribed, or scored.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Real-time features depend on your network, capture device, and third-party services outside our control.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Features may change, improve, or be discontinued over time.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>CAIRE may suspend service for maintenance, security, or legal reasons.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Unless agreed in an enterprise contract, no service level agreement or uptime commitment applies.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">15. Termination</h2>
            <p className="mb-4">You may stop using CAIRE at any time.</p>
            <p className="mb-4">CAIRE may suspend or terminate access if:</p>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You violate these Terms</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You monitor Hosts without informing them, or otherwise misuse the monitoring features</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You misuse AI, scoring, messaging, or viewer data</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Required by law or by the rules of a connected platform</span>
              </li>
            </ul>
            <p className="mt-4">Upon termination, your data is deleted or returned in line with the Privacy Policy and any applicable Data Processing Addendum. Financial records may be retained where law requires.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">16. Disclaimer of Warranties</h2>
            <p className="mb-4">The Services are provided "as is" and "as available".</p>
            <p className="mb-4">CAIRE does not guarantee:</p>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Transcription, speaker attribution, detection, or scoring accuracy</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Delivery of any given alert, or delivery within any particular time</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>The accuracy or completeness of GMV, order, or commercial data sourced from third-party platforms</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Any particular improvement in sales, conversion, or host performance</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Compatibility with your streaming setup, capture device, or environment</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>That the Service is free of interruptions or errors</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">17. Limitation of Liability (Important)</h2>
            <Card className="p-6 bg-card/40 border-primary/20">
              <p className="mb-4">To the maximum extent permitted by law:</p>
              <ul className="space-y-3 ml-5">
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span>CAIRE is not liable for any indirect, incidental, special, consequential, or punitive damages.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span>CAIRE's total liability is limited to the amount paid in the last 12 months or €100, whichever is greater.</span>
                </li>
              </ul>
              <p className="mt-4 mb-4">CAIRE is not liable for:</p>
              <ul className="space-y-3 ml-5">
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span>Inaccurate transcripts, speaker labels, scores, alerts, or AI-generated summaries</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span>Missed or delayed alerts, or sessions that were not captured</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span>Employment, pay, disciplinary, or contractual decisions you take about a Host</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span>Incorrect bonus or payout figures arising from rules or data you configured</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span>Lost data, revenue, or profits</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1 flex-shrink-0">•</span>
                  <span>Third-party platform actions, API changes, or system failures</span>
                </li>
              </ul>
              <p className="mt-4 text-sm">Nothing in these Terms excludes liability that cannot be excluded under Dutch law, including liability for intent or deliberate recklessness. Liability arising under the GDPR or under a Data Processing Addendum between us is governed by that Addendum and by applicable data protection law, not by this section.</p>
            </Card>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">18. Indemnification</h2>
            <p className="mb-4">You agree to indemnify CAIRE against claims arising from:</p>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Misuse of the Services</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Monitoring Hosts without lawful basis or without informing them</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Enabling speaker detection without a valid Article 9 condition, without giving Hosts specific prior notice, or in a jurisdiction where biometric identification is not permitted</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Employment or payment disputes with your Hosts</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Violation of laws, platform terms, or third-party rights</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Uploading harmful, illegal, or unauthorized content, including documents you were not entitled to hold</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Misuse of viewer data or messaging channels</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">19. Governing Law and Disputes</h2>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>These Terms are governed by the laws of the Netherlands.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Disputes will be resolved exclusively by the courts of Rotterdam, The Netherlands, unless otherwise required by applicable law.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Mandatory consumer and employment protections available to you under the law of your country of residence are not affected.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">20. Changes to Terms</h2>
            <p>We may update these Terms to reflect product or legal changes. Continued use after updates means you accept the revised Terms. We will give advance notice of material changes affecting monitoring, data retention, or fees.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">21. General</h2>
            <ul className="space-y-3 ml-5">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>If any provision of these Terms is held invalid or unenforceable, it will be limited or removed to the minimum extent necessary and the remaining provisions stay in full force.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>Where a signed enterprise agreement, order form, or Data Processing Addendum conflicts with these Terms, that document prevails for the subject matter it covers.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>A failure to enforce any provision is not a waiver of the right to enforce it later.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span>You may not assign these Terms without our written consent. We may assign them to an affiliate or in connection with a merger, acquisition, or sale of assets.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-foreground">22. Contact</h2>
            <p className="mb-4">For any legal questions:</p>
            <Card className="p-6 bg-card/30">
              <p className="font-semibold text-foreground mb-2">CAIRE B.V.</p>
              <p className="text-muted-foreground">Van der Takstraat 8<br />3071LL Rotterdam<br />The Netherlands</p>
              <a href="mailto:legal@meetcaire.com" className="text-primary hover:underline flex items-center gap-2 mt-4">
                <Mail className="w-4 h-4" />
                legal@meetcaire.com
              </a>
            </Card>
          </section>

        </div>

        {/* Footer Note */}
        <div className="mt-16 pt-8 border-t border-border text-center">
          <p className="text-sm text-muted-foreground">
            Last updated: September 8, 2026
          </p>
        </div>
      </main>
    </LegalPageChrome>
  );
};

export default Terms;
